import React from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import {
  LayoutDashboard, Users, Receipt, UserCircle,
  ArrowLeftRight, LogOut, X, Plus, Shield,
  FolderOpen, BarChart3, Repeat, Sparkles, Search, Bookmark
} from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { useI18n } from '../../contexts/I18nContext'
import toast from 'react-hot-toast'
import clsx from 'clsx'

const platformAdminItems = [
  { to: '/platform',             icon: LayoutDashboard, label: 'Overview' },
  { to: '/platform/users',       icon: Users,           label: 'Users' },
  { to: '/platform/groups',      icon: FolderOpen,      label: 'Groups' },
  { to: '/platform/expenses',    icon: Receipt,         label: 'Expenses' },
  { to: '/platform/settlements', icon: ArrowLeftRight,  label: 'Settlements' },
  { to: '/platform/analytics',   icon: BarChart3,       label: 'Analytics' },
]

export default function Sidebar({ onClose }) {
  const { user, logout } = useAuth()
  const { t } = useI18n()
  const navigate = useNavigate()
  const isPlatformAdmin = !!user?.is_platform_admin

  const navItems = [
    { to: '/dashboard', icon: LayoutDashboard, label: t('nav.dashboard') },
    { to: '/groups',    icon: Users,           label: t('nav.groups') },
    { to: '/expenses',  icon: Search,          label: 'Search' },
    { to: '/friends',   icon: UserCircle,      label: t('nav.friends') },
    { to: '/settle',    icon: ArrowLeftRight,  label: t('nav.settle_up') },
    { to: '/recurring', icon: Repeat,          label: t('nav.recurring') },
    { to: '/templates', icon: Bookmark,        label: 'Templates' },
    { to: '/insights',  icon: Sparkles,        label: 'Insights' },
  ]

  const handleLogout = async () => {
    try {
      await logout()
      navigate('/')
    } catch {
      toast.error('Failed to log out')
    }
  }

  const renderNavLink = ({ to, icon: Icon, label }, end = false) => (
    <NavLink
      key={to}
      to={to}
      end={end}
      onClick={onClose}
      className={({ isActive }) =>
        clsx(
          'flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150',
          isActive
            ? 'bg-forest-700 text-white'
            : 'text-forest-300 hover:bg-forest-800 hover:text-white'
        )
      }
    >
      <Icon size={18} />
      {label}
    </NavLink>
  )

  return (
    <div className="h-full bg-forest-900 text-white flex flex-col">

      {/* Logo + close */}
      <div className="flex items-center justify-between px-6 py-5 border-b border-forest-700/50">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-gold-500 flex items-center justify-center">
            <span className="text-forest-900 font-display font-800 text-sm leading-none">T</span>
          </div>
          <span className="font-display font-700 text-xl tracking-tight">tabowe</span>
        </div>
        <button
          onClick={onClose}
          className="lg:hidden text-forest-300 hover:text-white p-1 rounded-lg hover:bg-forest-700/50 transition-colors"
        >
          <X size={18} />
        </button>
      </div>

      {/* Add expense CTA */}
      <div className="px-4 pt-5">
        <button
          onClick={() => { navigate('/expenses/add'); onClose?.() }}
          className="w-full flex items-center justify-center gap-2 bg-gold-500 hover:bg-gold-400
                     text-forest-900 font-semibold text-sm py-2.5 rounded-xl transition-all
                     active:scale-95 shadow-sm"
        >
          <Plus size={16} />
          {t('nav.add_expense')}
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 pt-4 space-y-0.5 overflow-y-auto">
        <p className="px-3 pb-2 text-2xs font-semibold uppercase tracking-widest text-forest-400">
          Menu
        </p>
        {navItems.map(item => renderNavLink(item))}

        {/* Platform Admin section */}
        {isPlatformAdmin && (
          <>
            <div className="pt-4 pb-2 px-3 flex items-center gap-2">
              <Shield size={12} className="text-gold-400" />
              <p className="text-2xs font-semibold uppercase tracking-widest text-gold-400">
                Platform Admin
              </p>
            </div>
            {platformAdminItems.map(item => renderNavLink(item, item.to === '/platform'))}
          </>
        )}
      </nav>

      {/* Bottom user section */}
      <div className="px-3 pb-4 border-t border-forest-700/50 pt-3 space-y-0.5">
        <NavLink
          to="/profile"
          onClick={onClose}
          className={({ isActive }) =>
            clsx(
              'flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all',
              isActive ? 'bg-forest-700 text-white' : 'text-forest-300 hover:bg-forest-800 hover:text-white'
            )
          }
        >
          <div className="w-7 h-7 rounded-full bg-gold-500 flex items-center justify-center text-forest-900 text-xs font-bold flex-shrink-0">
            {user?.name?.[0]?.toUpperCase() || 'U'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="truncate font-medium text-white text-sm leading-tight">{user?.name || 'My Profile'}</p>
            <p className="truncate text-2xs text-forest-400">{user?.email}</p>
          </div>
        </NavLink>

        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium
                     text-forest-300 hover:bg-red-900/30 hover:text-red-300 transition-all"
        >
          <LogOut size={18} />
          {t('nav.sign_out')}
        </button>
      </div>
    </div>
  )
}
